/**
 * Employee Service — employee CRUD for the current tenant.
 *
 * Calls GET/POST /api/employees and POST /api/employees/batch on the chat-api Lambda.
 * Auth: Cognito JWT via authService.getIdToken().
 */

import authService from './authService';
import { API_CONFIG } from '../config/api';

// ─── Types ─────────────────────────────────────────────────────────

export interface Employee {
  employee_id: string;
  tenant_id: string;
  email: string;
  displayName: string;
  externalProvider: string;
  externalId: string;
  department?: string;
  jobTitle?: string;
  niwtStatus: 'pending' | 'active' | 'departed';
  createdAt: string;
  updatedAt: string;
}

export interface SkippedEmployee {
  email: string;
  reason: string;
  employeeId: string;
}

export interface BatchCreateResult {
  created: Employee[];
  skipped: SkippedEmployee[];
}

export interface BatchCreateUser {
  email: string;
  displayName: string;
  externalId: string;
  department?: string;
  title?: string;
}

export interface ListEmployeesResult {
  employees: Employee[];
  cursor?: string;
}

// ─── HTTP helper ───────────────────────────────────────────────────

async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await authService.getIdToken();

  const response = await fetch(`${API_CONFIG.BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    const message = errorBody.error || `Request failed with status ${response.status}`;
    const error = new Error(message) as any;
    error.errorCode = errorBody.error_code;
    error.status = response.status;
    throw error;
  }

  return response.json();
}

// ─── Service methods ───────────────────────────────────────────────

/**
 * List employees for the current tenant.
 * Supports pagination via limit + cursor.
 */
export async function listEmployees(
  options: { limit?: number; cursor?: string } = {}
): Promise<ListEmployeesResult> {
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);

  const qs = params.toString();
  return apiRequest<ListEmployeesResult>(`/api/employees${qs ? `?${qs}` : ''}`);
}

/**
 * Batch create employees. Admin/owner only.
 * Idempotent: duplicates (by tenant+provider+externalId) are returned as skipped.
 * Max 25 per batch (backend enforced).
 */
export async function batchCreateEmployees(
  source: string,
  users: BatchCreateUser[]
): Promise<BatchCreateResult> {
  return apiRequest<BatchCreateResult>('/api/employees/batch', {
    method: 'POST',
    body: JSON.stringify({ source, users }),
  });
}

/**
 * Create a single employee. Admin/owner only.
 * Idempotent: returns existing record if duplicate.
 */
export async function createEmployee(user: BatchCreateUser & { source?: string }): Promise<Employee> {
  return apiRequest<Employee>('/api/employees', {
    method: 'POST',
    body: JSON.stringify(user),
  });
}

const employeeService = {
  listEmployees,
  batchCreateEmployees,
  createEmployee,
};

export default employeeService;
